import React, { useState, useEffect, useCallback } from 'react';
import gateApi from '../api';
import { Spinner } from '../components/UI';

function todayISO() {
  return new Date().toLocaleDateString('en-CA'); // yyyy-mm-dd in local tz
}
function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString('en-CA');
}

// Ranges are inclusive of both ends, so "last 7 days" is today plus the six
// before it — seven dates on the calendar, not eight.
const RANGE_PRESETS = [
  { id: 'today',  label: 'Today',        from: todayISO,        to: todayISO },
  { id: '7d',     label: 'Last 7 days',  from: () => daysAgoISO(6),  to: todayISO },
  { id: '30d',    label: 'Last 30 days', from: () => daysAgoISO(29), to: todayISO },
  { id: 'custom', label: 'Custom' },
];

const ACCENT = '#5B21B6';

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

const card = {
  background: '#fff', border: '1px solid var(--border)', borderRadius: 14,
  padding: '18px 20px', marginBottom: 14,
};
const lbl = {
  fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
  color: 'var(--text-muted)', fontWeight: 600,
};

/**
 * Charity tab — stone given away, over a date range.
 *
 * Shows trips rather than tonnes because a charity vehicle is never weighed;
 * the gate records what left and where it went, and that is all there is to
 * count. Ranged on the exit time the operator typed, so a load entered in the
 * evening still belongs to the day it actually went out.
 */
export default function Charity() {
  const [preset, setPreset] = useState('today');
  const [from, setFrom] = useState(todayISO());
  const [to, setTo]     = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async (f, t) => {
    setLoading(true);
    setErr(null);
    try {
      setData(await gateApi.getCharityDispatches({ from: f, to: t }));
    } catch (e) {
      setErr(e?.response?.status === 403
        ? 'Your login cannot view charity records.'
        : 'Could not load charity records.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(from, to); }, [load, from, to]);

  function pick(p) {
    setPreset(p.id);
    if (p.id === 'custom') return;
    setFrom(p.from());
    setTo(p.to());
  }

  return (
    <div style={{ padding: '18px 16px 40px' }}>

      {/* ── Range picker ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {RANGE_PRESETS.map(p => {
          const on = preset === p.id;
          return (
            <button key={p.id} onClick={() => pick(p)} style={{
              padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              borderRadius: 9, border: `1px solid ${on ? ACCENT : 'var(--border)'}`,
              background: on ? ACCENT : '#fff', color: on ? '#fff' : 'var(--text-muted)',
            }}>{p.label}</button>
          );
        })}
      </div>

      {preset === 'custom' && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)}
                 style={{ padding: '9px 11px', borderRadius: 9, border: '1px solid var(--border)',
                          fontSize: 13, fontFamily: 'var(--font-mono)' }} />
          <input type="date" value={to} min={from} max={todayISO()} onChange={e => setTo(e.target.value)}
                 style={{ padding: '9px 11px', borderRadius: 9, border: '1px solid var(--border)',
                          fontSize: 13, fontFamily: 'var(--font-mono)' }} />
        </div>
      )}

      {loading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner /></div>}

      {!loading && err && (
        <div style={{ ...card, borderColor: '#E9B4B4', background: '#FDF2F2' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#B91C1C', marginBottom: 10 }}>{err}</div>
          <button onClick={() => load(from, to)} style={{
            padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            borderRadius: 9, border: '1px solid var(--border)', background: '#fff',
          }}>Try again</button>
        </div>
      )}

      {!loading && !err && data && (
        <>
          {/* ── Headline ──────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ ...card, flex: 1, minWidth: 150, marginBottom: 0 }}>
              <div style={lbl}>Loads given</div>
              <div style={{ fontSize: 30, fontWeight: 700, fontFamily: 'var(--font-mono)', color: ACCENT }}>
                {data.totalTrips}
              </div>
            </div>
            <div style={{ ...card, flex: 1, minWidth: 150, marginBottom: 0 }}>
              <div style={lbl}>Vehicles</div>
              <div style={{ fontSize: 30, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                {data.distinctVehicles}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>different number plates</div>
            </div>
          </div>

          {data.totalTrips === 0 ? (
            <div style={{ ...card, textAlign: 'center', padding: '36px 20px' }}>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Nothing given away in this period</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Charity vehicles are recorded from the charity form at the gate.
              </div>
            </div>
          ) : (
            <>
              {/* ── By material ───────────────────────────────────────── */}
              <div style={card}>
                <div style={{ ...lbl, marginBottom: 14 }}>By material</div>
                <Breakdown rows={data.materials} nameKey="material" max={data.totalTrips} />
              </div>

              {/* ── By destination. The question an owner is actually asked
                      at the end of the year is who received it, not what. ── */}
              <div style={card}>
                <div style={{ ...lbl, marginBottom: 14 }}>Where it went</div>
                <Breakdown rows={data.destinations} nameKey="destination" max={data.totalTrips} />
              </div>

              {/* ── Every load ────────────────────────────────────────── */}
              <div style={card}>
                <div style={{ ...lbl, marginBottom: 12 }}>Every load · {data.totalTrips}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {data.dispatches.map(d => (
                    <div key={d.id} style={{
                      borderBottom: '1px solid var(--border)', paddingBottom: 10,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14 }}>
                          {d.licensePlate}
                        </span>
                        <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                          {fmtDateTime(d.exitTime)}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 3 }}>
                        {d.material}
                        {d.destination && <> → <strong style={{ fontWeight: 600 }}>{d.destination}</strong></>}
                      </div>
                      {d.loadingFrom && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                          Loaded at {d.loadingFrom}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Counted by the time the vehicle left, not when the entry was typed.
            Charity loads are not weighed, so these are loads given rather than
            tonnes, and none of them appear in the sales or tonnage reports.
          </div>
        </>
      )}
    </div>
  );
}

// Shared bar list. Count and share come from the backend so this and the
// material report cannot drift apart on how a percentage is worked out.
function Breakdown({ rows, nameKey, max }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map(r => (
        <div key={r[nameKey]}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 5 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{r[nameKey]}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, whiteSpace: 'nowrap' }}>
              {r.trips} <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>({r.sharePct}%)</span>
            </span>
          </div>
          <div style={{ height: 9, background: '#F1EFE8', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{
              width: `${(r.trips / (max || 1)) * 100}%`, height: '100%',
              background: ACCENT, borderRadius: 5, minWidth: 3,
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}
